// Subscriber Role Management
// Defines roles and permissions within a subscriber organization

// Role constants
export const SUBSCRIBER_ROLES = {
  USER: 'user',
  MANAGER: 'manager',
  ADMIN: 'admin',
};

// Does this session bypass role checks entirely?
//
// A system admin normally short-circuits every permission check. That blanket
// allow is exactly what has to be suppressed while "view as" is active —
// otherwise picking a lesser role changes nothing and the mode is a lie. Kept
// here as a named rule rather than repeated inline in each check, because
// getting it wrong in one of them is silent: the UI just looks right.
//
// This is a *display* concern only. The token still carries the real claims,
// and the API and firestore.rules never consult this.
export const grantsEverything = (isSystemAdmin, simulatedRole) =>
  Boolean(isSystemAdmin) && !simulatedRole;

// Role hierarchy (higher index = more permissions)
export const ROLE_HIERARCHY = [
  SUBSCRIBER_ROLES.USER,
  SUBSCRIBER_ROLES.MANAGER,
  SUBSCRIBER_ROLES.ADMIN,
];

// Permission constants
export const PERMISSIONS = {
  // Task permissions
  VIEW_OWN_TASKS: 'view_own_tasks',
  VIEW_ALL_TASKS: 'view_all_tasks',
  APPROVE_TASKS: 'approve_tasks',
  ASSIGN_TASKS: 'assign_tasks',
  EXPORT_TASKS: 'export_tasks',

  // Operator permissions
  VIEW_OPERATORS: 'view_operators',
  MANAGE_OPERATORS: 'manage_operators',
  RESET_OPERATOR_PINS: 'reset_operator_pins',

  // Team permissions
  VIEW_TEAMS: 'view_teams',
  MANAGE_TEAMS: 'manage_teams',

  // Customer permissions
  VIEW_CUSTOMERS: 'view_customers',
  MANAGE_CUSTOMERS: 'manage_customers',

  // Template permissions
  VIEW_TEMPLATES: 'view_templates',
  MANAGE_TEMPLATES: 'manage_templates',

  // Asset permissions
  VIEW_ASSETS: 'view_assets',
  MANAGE_ASSETS: 'manage_assets',

  // Issue permissions
  VIEW_ISSUES: 'view_issues',
  MANAGE_ISSUES: 'manage_issues',
  REPORT_ISSUES: 'report_issues',

  // Settings permissions
  VIEW_SETTINGS: 'view_settings',
  VIEW_USAGE_STATS: 'view_usage_stats',
  // Read-only view of the audit trail. Admin + manager; the trail itself is
  // append-only, so there is no matching "edit" permission.
  VIEW_AUDIT_LOG: 'view_audit_log',

  // Admin-only permissions
  MANAGE_USERS: 'manage_users',
  MANAGE_USER_ROLES: 'manage_user_roles',
  MANAGE_SECURITY: 'manage_security',
  MANAGE_BILLING: 'manage_billing',
  PERMANENT_DELETE: 'permanent_delete',
  MANAGE_INTEGRATIONS: 'manage_integrations',
  EXPORT_ALL_DATA: 'export_all_data',
  MANAGE_EMAIL_TEMPLATES: 'manage_email_templates',
  MANAGE_PDF_TEMPLATES: 'manage_pdf_templates',
};

// Role to permissions mapping
const ROLE_PERMISSIONS = {
  [SUBSCRIBER_ROLES.USER]: [
    PERMISSIONS.VIEW_OWN_TASKS,
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ISSUES,
    PERMISSIONS.REPORT_ISSUES,
  ],

  [SUBSCRIBER_ROLES.MANAGER]: [
    // Inherits all USER permissions
    PERMISSIONS.VIEW_OWN_TASKS,
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ISSUES,
    PERMISSIONS.REPORT_ISSUES,

    // Additional MANAGER permissions
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.APPROVE_TASKS,
    PERMISSIONS.ASSIGN_TASKS,
    PERMISSIONS.EXPORT_TASKS,
    PERMISSIONS.VIEW_OPERATORS,
    PERMISSIONS.MANAGE_OPERATORS,
    PERMISSIONS.RESET_OPERATOR_PINS,
    PERMISSIONS.VIEW_TEAMS,
    PERMISSIONS.MANAGE_TEAMS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.MANAGE_TEMPLATES,
    // Email and PDF templates are templates: the API, firestore.rules and the
    // routes all draw the admin-or-manager line (ELEVATED_ROLES in api deps.py).
    PERMISSIONS.MANAGE_EMAIL_TEMPLATES,
    PERMISSIONS.MANAGE_PDF_TEMPLATES,
    PERMISSIONS.MANAGE_ASSETS,
    PERMISSIONS.MANAGE_ISSUES,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.VIEW_USAGE_STATS,
    PERMISSIONS.VIEW_AUDIT_LOG,
  ],

  [SUBSCRIBER_ROLES.ADMIN]: [
    // Inherits all MANAGER permissions
    PERMISSIONS.VIEW_OWN_TASKS,
    PERMISSIONS.VIEW_ALL_TASKS,
    PERMISSIONS.APPROVE_TASKS,
    PERMISSIONS.ASSIGN_TASKS,
    PERMISSIONS.EXPORT_TASKS,
    PERMISSIONS.VIEW_OPERATORS,
    PERMISSIONS.MANAGE_OPERATORS,
    PERMISSIONS.RESET_OPERATOR_PINS,
    PERMISSIONS.VIEW_TEAMS,
    PERMISSIONS.MANAGE_TEAMS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_TEMPLATES,
    PERMISSIONS.MANAGE_TEMPLATES,
    PERMISSIONS.MANAGE_EMAIL_TEMPLATES,
    PERMISSIONS.MANAGE_PDF_TEMPLATES,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.MANAGE_ASSETS,
    PERMISSIONS.VIEW_ISSUES,
    PERMISSIONS.MANAGE_ISSUES,
    PERMISSIONS.REPORT_ISSUES,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.VIEW_USAGE_STATS,

    // Admin-only permissions
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_USER_ROLES,
    PERMISSIONS.MANAGE_SECURITY,
    PERMISSIONS.MANAGE_BILLING,
    PERMISSIONS.VIEW_AUDIT_LOG,
    PERMISSIONS.PERMANENT_DELETE,
    PERMISSIONS.MANAGE_INTEGRATIONS,
    PERMISSIONS.EXPORT_ALL_DATA,
  ],
};

/**
 * Check if a role has a specific permission
 * @param {string} role - The user's role
 * @param {string} permission - The permission to check
 * @returns {boolean}
 */
export function hasPermission(role, permission) {
  if (!role || !permission) return false;
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
}

/**
 * Check if a role has any of the specified permissions
 * @param {string} role - The user's role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export function hasAnyPermission(role, permissions) {
  if (!role || !permissions || permissions.length === 0) return false;
  return permissions.some(permission => hasPermission(role, permission));
}

/**
 * Check if a role has all of the specified permissions
 * @param {string} role - The user's role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export function hasAllPermissions(role, permissions) {
  if (!role || !permissions || permissions.length === 0) return false;
  return permissions.every(permission => hasPermission(role, permission));
}

/**
 * Check if role1 is equal or higher than role2 in hierarchy
 * @param {string} role1 - The role to check
 * @param {string} role2 - The role to compare against
 * @returns {boolean}
 */
export function isRoleAtLeast(role1, role2) {
  const index1 = ROLE_HIERARCHY.indexOf(role1);
  const index2 = ROLE_HIERARCHY.indexOf(role2);
  if (index1 === -1 || index2 === -1) return false;
  return index1 >= index2;
}

/**
 * Get all permissions for a role
 * @param {string} role - The user's role
 * @returns {string[]}
 */
export function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Get display label for a role
 * @param {string} role - The role constant
 * @returns {string}
 */
export function getRoleLabel(role) {
  const labels = {
    [SUBSCRIBER_ROLES.USER]: 'User',
    [SUBSCRIBER_ROLES.MANAGER]: 'Manager',
    [SUBSCRIBER_ROLES.ADMIN]: 'Admin',
  };
  return labels[role] || role;
}

/**
 * Get role description
 * @param {string} role - The role constant
 * @returns {string}
 */
export function getRoleDescription(role) {
  const descriptions = {
    [SUBSCRIBER_ROLES.USER]: 'View access to tasks and basic operations',
    [SUBSCRIBER_ROLES.MANAGER]: 'Day-to-day operations including managing operators, teams, templates, and tasks',
    [SUBSCRIBER_ROLES.ADMIN]: 'Full access including user management, security settings, and audit logs',
  };
  return descriptions[role] || '';
}

/**
 * Get MUI color for role chip
 * @param {string} role - The role constant
 * @returns {string}
 */
export function getRoleColor(role) {
  const colors = {
    [SUBSCRIBER_ROLES.USER]: 'default',
    [SUBSCRIBER_ROLES.MANAGER]: 'primary',
    [SUBSCRIBER_ROLES.ADMIN]: 'error',
  };
  return colors[role] || 'default';
}
